import { NavLinkProps } from "@/types";
import { useNavigate, useLocation } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const NavLinks = ({ scrollToSection, isMobile = false }: NavLinkProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSectionNav = (path: string) => {
    const [routePath, hash] = path.split("#");
    const targetPath = routePath || "/";
    if (location.pathname === targetPath && hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", `${targetPath}#${hash}`);
    } else {
      navigate(path);
    }
  };


  // Soluciones agrupa Plataformas + Servicios en un solo dropdown
  const plataformasItems = [
    { title: "Nexus ATS", path: "/nexus-ats", description: "Sistema de reclutamiento con IA" },
    { title: "Employee Hub+", path: "/agente", description: "Workforce Platform: administración integral de personal" },
    { title: "Elempleo.com", path: "/elempleo", description: "Bolsa de empleo +1M candidatos" },
    { title: "Orienta360", path: "/orientacion-vocacional", description: "Orientación vocacional y perfil conductual" },
    { title: "Nexus Languages", path: "/nexus-languages", description: "Evaluación de inglés (MCER)" },
    { title: "Talent Connect", path: "/talent-connect", description: "Ferias de empleo virtuales" },
  ];

  const serviciosItems = [
    { title: "Reclutamiento y Selección", path: "/reclutamiento-seleccion", description: "Headhunting especializado" },
    { title: "Pruebas Psicométricas", path: "/pruebas-psicometricas", description: "Evaluación de talento" },
    { title: "Encuesta Salarial", path: "/encuesta-salarial", description: "Datos salariales del mercado CR" },
    { title: "Encuesta de Clima y Cultura", path: "/encuesta-clima-cultura", description: "Diagnóstico organizacional con IA" },
  ];

  const solucionesItems = [...plataformasItems, ...serviciosItems];

  const candidatosItems = [
    { title: "Registrar CV", path: "/registrar-cv", description: "Únete a nuestra base" },
    { title: "Ofertas de Empleo", path: "/ofertas-empleo", description: "Oportunidades laborales" },
  ];

  const nosotrosItems = [
    { title: "Inicio", path: "/inicio", description: "Bienvenida a Nexus HR" },
    { title: "Cómo funciona", path: "/como-funciona", description: "El proceso paso a paso" },
    { title: "Soluciones", path: "/soluciones", description: "Plataformas y servicios" },
    { title: "Resultados", path: "/resultados", description: "Casos y métricas reales" },
    { title: "Por qué Nexus", path: "/por-que-nexus", description: "Nuestra propuesta de valor" },
    { title: "Partners e integraciones", path: "/partners", description: "Con quiénes trabajamos" },
    { title: "Preguntas frecuentes", path: "/preguntas-frecuentes", description: "Resolvé tus dudas" },
    { title: "Agendar demo", path: "/agendar-demo", description: "Reservá una asesoría" },
  ];

  const recursosItems = [
    { title: "Descargables", path: "/descargables", description: "Guías y plantillas gratuitas" },
    { title: "Blog", path: "https://blog.nexushr.tech/", description: "Artículos y tendencias HR", isExternal: true },
  ];

  const solucionesRoutes = solucionesItems.map(i => i.path);
  const recursosRoutes = recursosItems.map(i => i.path);

  const isSolucionesActive = solucionesRoutes.some(route => location.pathname === route);
  const isNosotrosActive = nosotrosItems.some(i => location.pathname === i.path);
  const isRecursosActive = recursosRoutes.some(route => location.pathname === route);
  const isInicioActive = location.pathname === "/";

  const getActiveClass = (isActive: boolean) =>
    isActive ? "text-primary font-semibold" : "text-foreground";

  const getActiveLinkClass = (isActive: boolean, isMobileView: boolean) => {
    const base = isMobileView
      ? "block w-full text-left py-2 pl-3 text-sm transition-colors"
      : "text-base transition-colors hover:text-primary";
    return `${base} ${isActive ? "text-primary font-semibold" : "text-foreground"}`;
  };

  if (isMobile) {
    return (
      <>
        {/* Inicio */}
        <div className="py-2">
          <button
            onClick={() => navigate("/")}
            aria-current={isInicioActive ? "page" : undefined}
            className={getActiveLinkClass(isInicioActive, true)}
          >
            Inicio
          </button>
        </div>

        {/* Soluciones - Plataformas */}
        <div className="py-2 border-t border-border/50">
          <span className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#620AE2" }}>
            Plataformas
          </span>
          {plataformasItems.map((item) => (
            <button
              key={item.title}
              onClick={() => navigate(item.path)}
              className={getActiveLinkClass(location.pathname === item.path, true)}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* Soluciones - Servicios */}
        <div className="py-2 border-t border-border/50">
          <span className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#620AE2" }}>
            Servicios
          </span>
          {serviciosItems.map((item) => (
            <button
              key={item.title}
              onClick={() => navigate(item.path)}
              className={getActiveLinkClass(location.pathname === item.path, true)}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* Nosotros */}
        <div className="py-2 border-t border-border/50">
          <span className="block text-xs font-semibold uppercase tracking-wider mb-2 text-muted-foreground">
            Nosotros
          </span>
          {nosotrosItems.map((item) => (
            <button
              key={item.title}
              onClick={() => handleSectionNav(item.path)}
              className={getActiveLinkClass(false, true)}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* Recursos */}
        <div className="py-2 border-t border-border/50 space-y-1">
          <span className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${isRecursosActive ? "text-primary" : "text-muted-foreground"}`}>
            Recursos
          </span>
          {recursosItems.map((item) => (
            item.isExternal ? (
              <a
                key={item.title}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className={getActiveLinkClass(false, true)}
              >
                {item.title}
              </a>
            ) : (
              <button
                key={item.title}
                onClick={() => navigate(item.path)}
                className={getActiveLinkClass(location.pathname === item.path, true)}
              >
                {item.title}
              </button>
            )
          ))}
        </div>

        {/* Universidades */}
        <div className="py-2 border-t border-border/50">
          <a
            href="https://campusjobs.nexushr.tech/"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-left py-2 pl-3 text-sm transition-colors text-foreground"
          >
            Universidades
          </a>
        </div>
      </>
    );
  }

  const renderColumn = (title: string, items: typeof plataformasItems) => (
    <div className="flex-1">
      <span
        className="block px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider select-none"
        style={{ color: "#620AE2" }}
      >
        {title}
      </span>
      <ul>
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <li key={item.title}>
              <button
                onClick={() => navigate(item.path)}
                className={`block w-full text-left px-4 py-2.5 rounded-md transition-all duration-200 group ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-[#620AE2] hover:text-white"
                }`}
              >
                <span className={`block text-sm font-semibold ${isActive ? "" : ""}`}>
                  {item.title}
                </span>
                <span className={`block text-xs mt-0.5 ${isActive ? "text-primary/80" : "text-muted-foreground group-hover:text-white/90"}`}>
                  {item.description}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <>
      {/* Inicio */}
      <button
        onClick={() => navigate("/")}
        aria-current={isInicioActive ? "page" : undefined}
        className={`relative text-base transition-colors hover:text-primary ${getActiveClass(isInicioActive)}`}
      >
        Inicio
        {isInicioActive && (
          <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
        )}
      </button>

      {/* Soluciones Dropdown (Plataformas + Servicios) */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger
              className={`h-auto p-0 text-base transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent hover:text-primary data-[state=open]:text-primary border-0 focus:bg-transparent relative ${getActiveClass(isSolucionesActive)}`}
            >
              Para Empresas
              {isSolucionesActive && (
                <span className="absolute -bottom-1 left-0 right-4 h-0.5 bg-primary rounded-full" />
              )}
            </NavigationMenuTrigger>
            <NavigationMenuContent className="bg-background/98 backdrop-blur-md border border-border/50 shadow-xl rounded-lg z-50 animate-in fade-in-0 zoom-in-95 duration-200">
              <div className="flex flex-col sm:flex-row w-[300px] sm:w-auto sm:min-w-[560px] p-3 gap-2 sm:divide-x divide-border/60">
                {renderColumn("Plataformas", plataformasItems)}
                {renderColumn("Servicios", serviciosItems)}
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      {/* Nosotros Dropdown (secciones del landing) */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger
              className={`h-auto p-0 text-base transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent hover:text-primary data-[state=open]:text-primary border-0 focus:bg-transparent relative ${getActiveClass(isNosotrosActive)}`}
            >
              Nosotros
            </NavigationMenuTrigger>
            <NavigationMenuContent className="bg-background/98 backdrop-blur-md border border-border/50 shadow-xl rounded-lg z-50 animate-in fade-in-0 zoom-in-95 duration-200">
              <ul className="w-[320px] p-2">
                {nosotrosItems.map((item) => (
                  <li key={item.title}>
                    <button
                      onClick={() => handleSectionNav(item.path)}
                      className="block w-full text-left px-4 py-3 rounded-md transition-all duration-200 text-foreground hover:bg-[#620AE2] hover:text-white group"
                    >
                      <span className="text-sm font-medium">{item.title}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5 group-hover:text-white/90">{item.description}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      {/* Recursos Dropdown */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger
              className={`h-auto p-0 text-base transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent hover:text-primary data-[state=open]:text-primary border-0 focus:bg-transparent relative ${getActiveClass(isRecursosActive)}`}
            >
              Recursos
              {isRecursosActive && (
                <span className="absolute -bottom-1 left-0 right-4 h-0.5 bg-primary rounded-full" />
              )}
            </NavigationMenuTrigger>
            <NavigationMenuContent className="bg-background/98 backdrop-blur-md border border-border/50 shadow-xl rounded-lg z-50 animate-in fade-in-0 zoom-in-95 duration-200">
              <ul className="w-[260px] p-2">
                {recursosItems.map((item) => {
                  const isActive = !item.isExternal && location.pathname === item.path;
                  return (
                    <li key={item.title}>
                      {item.isExternal ? (
                        <a
                          href={item.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full text-left px-4 py-3 rounded-md transition-all duration-200 text-foreground hover:bg-[#620AE2] hover:text-white"
                        >
                          <span className="text-sm">{item.title}</span>
                          <span className="block text-xs text-muted-foreground mt-0.5">{item.description}</span>
                        </a>
                      ) : (
                        <button
                          onClick={() => navigate(item.path)}
                          className={`block w-full text-left px-4 py-3 rounded-md transition-all duration-200 ${
                            isActive
                              ? "bg-primary/10 text-primary"
                              : "text-foreground hover:bg-[#620AE2] hover:text-white"
                          }`}
                        >
                          <span className={`text-sm ${isActive ? "font-medium" : ""}`}>{item.title}</span>
                          <span className="block text-xs text-muted-foreground mt-0.5">{item.description}</span>
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      {/* Universidades (external) */}
      <a
        href="https://campusjobs.nexushr.tech/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-base transition-colors hover:text-primary text-foreground"
      >
        Universidades
      </a>
    </>
  );
};

export default NavLinks;
